import type { MotionValue } from 'framer-motion';
import type { RefObject } from 'react';

export const SCROLL_COLLAPSE_PROGRESS = {
  Expanded: 0,
  Collapsed: 1,
} as const;

const SCROLL_DIRECTION = {
  Up: 'up',
  Down: 'down',
  Still: 'still',
} as const;

type ScrollDirection = typeof SCROLL_DIRECTION[keyof typeof SCROLL_DIRECTION];

interface ApplyScrollCollapseInput {
  delta: number;
  value: MotionValue<number>;
  upwardAccumRef: RefObject<number>;
  currentRef: RefObject<number>;
  expandAccumulationPx: number;
}

type ScrollDirectionHandler = (input: ApplyScrollCollapseInput) => void;

const SCROLL_DIRECTION_HANDLER: Record<ScrollDirection, ScrollDirectionHandler> = {
  [SCROLL_DIRECTION.Down]: collapseOnDownwardScroll,
  [SCROLL_DIRECTION.Up]: expandAfterSustainedUpwardScroll,
  [SCROLL_DIRECTION.Still]: () => undefined,
};

export function applyScrollCollapseProgress(
  input: ApplyScrollCollapseInput,
): void {
  SCROLL_DIRECTION_HANDLER[getScrollDirection(input.delta)](input);
}

function getScrollDirection(delta: number): ScrollDirection {
  if (delta > 0) return SCROLL_DIRECTION.Down;
  if (delta < 0) return SCROLL_DIRECTION.Up;
  return SCROLL_DIRECTION.Still;
}

function collapseOnDownwardScroll({
  value,
  upwardAccumRef,
  currentRef,
}: ApplyScrollCollapseInput): void {
  upwardAccumRef.current = 0;
  if (currentRef.current === SCROLL_COLLAPSE_PROGRESS.Collapsed) return;

  currentRef.current = SCROLL_COLLAPSE_PROGRESS.Collapsed;
  value.set(SCROLL_COLLAPSE_PROGRESS.Collapsed);
}

function expandAfterSustainedUpwardScroll({
  delta,
  value,
  upwardAccumRef,
  currentRef,
  expandAccumulationPx,
}: ApplyScrollCollapseInput): void {
  upwardAccumRef.current += -delta;
  if (upwardAccumRef.current < expandAccumulationPx) return;
  if (currentRef.current === SCROLL_COLLAPSE_PROGRESS.Expanded) return;

  upwardAccumRef.current = 0;
  currentRef.current = SCROLL_COLLAPSE_PROGRESS.Expanded;
  value.set(SCROLL_COLLAPSE_PROGRESS.Expanded);
}
